import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { chartDateRangeOptions } from '@/modules/common/constants/chart-date-range-oprions.ts';
import { DATE_RANGE_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerDashboardWrapperOutletContext } from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import { mapDateRangeToObj } from '@/modules/issuer-dashboard/helpers/mapDateRangeToObj.ts';
import { normaliseNavData } from '@/modules/issuer-dashboard/helpers/normalizeNavData.ts';
import { useNavHistoryListQuery } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { BalanceChart, ChartEmptyState, InfoCard, SelectButtons } from 'ui';
import { ApexChartSeries } from 'ui/types';

const chartOptions: Partial<ApexOptions> = {
  grid: {
    show: true,
    padding: {
      bottom: 0,
      right: 0,
    },
  },
  markers: {
    size: 6,
  },
  legend: {
    position: 'top',
    horizontalAlign: 'left',
    markers: {
      size: 8,
      offsetX: -5,
      offsetY: 0,
    },
    itemMargin: { horizontal: 8 },
  },
  stroke: {
    width: 2,
    lineCap: 'round',
  },
  colors: [
    '#0369A1',
    '#0278B5',
    '#0389C7',
    '#01A0D8',
    '#25B0E5',
    '#4BC1F1',
    '#77D1F9',
    '#77D1F9',
    '#77D1F9',
    '#A6E1FD',
  ],
  xaxis: {
    tickAmount: 6,
    labels: {
      rotate: 0,
      formatter: (date: string) => {
        return dayjs(date).format('DD-MM');
      },
      style: {
        fontSize: '12px',
        fontWeight: '400',
        colors: '#A3A3A3',
      },
    },
    tooltip: {
      enabled: false,
    },
  },
};

interface NavProps {
  showFullName?: boolean;
  withFuture?: boolean;
}

const Nav = ({ showFullName = true, withFuture = true }: NavProps) => {
  const [params, setParams] = useSearchParams();
  const { registerId } = useOutletContext<IssuerDashboardWrapperOutletContext>();
  const date_range = params.get(DATE_RANGE_SEARCH_PARAM_KEY + '1') || 'all';
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const { data, isLoading, isError } = useNavHistoryListQuery(
    mapDateRangeToObj(date_range || '', withFuture),
    false,
    registerId,
  );

  const handleChangeDateRange = useCallback(
    (date_range: string) => {
      params.set(DATE_RANGE_SEARCH_PARAM_KEY + '1', date_range);
      setParams(params);
    },
    [params, setParams],
  );

  const chartSeries = useMemo<ApexChartSeries[]>(() => normaliseNavData(data), [data]);

  const isPresentChartRecords = useMemo(
    () => chartSeries.some(({ data }) => data.length),
    [chartSeries],
  );
  //
  return (
    <InfoCard
      title={`${getRegisterTypeStrings('nav')}${showFullName ? ' History' : ''}`}
      headerAction={
        <SelectButtons
          options={chartDateRangeOptions}
          selected={chartDateRangeOptions.find(({ value }) => value === date_range)}
          onChange={handleChangeDateRange}
          className="sm:w-[375px] w-[300px]"
          disabled={isLoading}
        />
      }
    >
      {!isLoading && isPresentChartRecords && (
        <BalanceChart
          data={chartSeries}
          height="300px"
          type="line"
          fill={false}
          withDateTime={true}
          options={chartOptions}
        />
      )}
      {!chartSeries?.length && (
        <div className="py-4 flex justify-center items-center font-medium text-lg">
          <ChartEmptyState isError={isError} isLoading={isLoading} />
        </div>
      )}
      {!isLoading && !isPresentChartRecords && (
        <div className="w-full h-[200px] flex items-center justify-center text-2xl py-6">
          No data yet
        </div>
      )}
    </InfoCard>
  );
};

export default Nav;
